import React from 'react';
import { Form } from 'react-bootstrap';

const VltFormFields = ({
  formData,
  formErrors,
  onChange,
  onTypeChange,
  onDocumentSourceChange,
  onVideoSourceChange,
  onOrganizationChange,
  onGroupToggle,
  organizations = [],
  groups = [],
  instructors = [],
  showOrganization = true,
  organizationDisabled = false,
  groupsDisabled = false
}) => {
  const handleFieldChange = (field, value) => {
    onChange({ ...formData, [field]: value });
  };

  const hasOrganization = Boolean(formData.organization_id);
  const hasGroups = (formData.group_ids || []).length > 0;
  const orgFieldDisabled = organizationDisabled || hasGroups;
  const groupFieldDisabled = groupsDisabled || hasOrganization;

  const handleOrganizationSelect = (orgId) => {
    if (onOrganizationChange) {
      onOrganizationChange(orgId);
      return;
    }
    onChange({
      ...formData,
      organization_id: orgId,
      group_ids: orgId ? [] : formData.group_ids,
      instructor_id: ''
    });
  };

  const handleGroupCheckbox = (groupId) => {
    if (onGroupToggle) {
      onGroupToggle(groupId);
      return;
    }
    const exists = (formData.group_ids || []).includes(groupId);
    onChange({
      ...formData,
      organization_id: exists ? formData.organization_id : '',
      group_ids: exists
        ? formData.group_ids.filter((id) => id !== groupId)
        : [...(formData.group_ids || []), groupId]
    });
  };

  const renderDocumentFields = () => (
    <div className="mb-4 p-4 rounded-xl border border-purple-200 bg-purple-50/40">
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        Document Source <span className="text-red-500">*</span>
      </label>
      <div className="flex flex-wrap gap-4 mb-3">
        <Form.Check
          type="radio"
          id="vlt-document-link"
          name="document_source"
          label="Document Link"
          checked={formData.document_source === 'link'}
          onChange={() => onDocumentSourceChange('link')}
        />
        <Form.Check
          type="radio"
          id="vlt-document-upload"
          name="document_source"
          label="Upload Document (PDF / Word)"
          checked={formData.document_source === 'upload'}
          onChange={() => onDocumentSourceChange('upload')}
        />
      </div>

      {formData.document_source === 'link' ? (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Document URL <span className="text-red-500">*</span>
          </label>
          <input
            type="url"
            value={formData.url_or_path}
            onChange={(e) => handleFieldChange('url_or_path', e.target.value)}
            className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${
              formErrors.url_or_path ? 'border-red-500' : 'border-purple-200 focus:ring-2 focus:ring-purple-500'
            }`}
            placeholder="https://example.com/document.pdf"
          />
          {formErrors.url_or_path && <p className="text-red-500 text-xs mt-1">{formErrors.url_or_path}</p>}
        </div>
      ) : (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Upload File <span className="text-red-500">*</span>
          </label>
          <input
            type="file"
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => handleFieldChange('document_file', e.target.files?.[0] || null)}
            className={`w-full px-3 py-2 rounded-lg border text-sm ${
              formErrors.document_file ? 'border-red-500' : 'border-purple-200'
            }`}
          />
          <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, DOC, DOCX (max 25MB)</p>
          {formData.existing_document_path && !formData.document_file && (
            <p className="text-xs text-purple-700 mt-1">Current file is uploaded. Choose a new file to replace it.</p>
          )}
          {formErrors.document_file && <p className="text-red-500 text-xs mt-1">{formErrors.document_file}</p>}
        </div>
      )}
    </div>
  );

  const renderVideoFields = () => (
    <div className="mb-4 p-4 rounded-xl border border-purple-200 bg-purple-50/40">
      <label className="block text-sm font-semibold text-gray-700 mb-2">
        Video Source <span className="text-red-500">*</span>
      </label>
      <div className="flex flex-wrap gap-4 mb-3">
        <Form.Check
          type="radio"
          id="vlt-video-link"
          name="video_source"
          label="Video Link"
          checked={formData.video_source === 'link'}
          onChange={() => onVideoSourceChange('link')}
        />
        <Form.Check
          type="radio"
          id="vlt-video-upload"
          name="video_source"
          label="Upload Video (MP4 / WebM / MOV)"
          checked={formData.video_source === 'upload'}
          onChange={() => onVideoSourceChange('upload')}
        />
      </div>

      {formData.video_source === 'link' ? (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Video URL <span className="text-red-500">*</span>
          </label>
          <input
            type="url"
            value={formData.url_or_path}
            onChange={(e) => handleFieldChange('url_or_path', e.target.value)}
            className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${
              formErrors.url_or_path ? 'border-red-500' : 'border-purple-200 focus:ring-2 focus:ring-purple-500'
            }`}
            placeholder="https://example.com/video.mp4"
          />
          {formErrors.url_or_path && <p className="text-red-500 text-xs mt-1">{formErrors.url_or_path}</p>}
        </div>
      ) : (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Upload Video <span className="text-red-500">*</span>
          </label>
          <input
            type="file"
            accept="video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov"
            onChange={(e) => handleFieldChange('video_file', e.target.files?.[0] || null)}
            className={`w-full px-3 py-2 rounded-lg border text-sm ${
              formErrors.video_file ? 'border-red-500' : 'border-purple-200'
            }`}
          />
          <p className="text-xs text-gray-500 mt-1">Accepted formats: MP4, WebM, MOV (max 100MB)</p>
          {formData.existing_video_path && !formData.video_file && (
            <p className="text-xs text-purple-700 mt-1">Current video is uploaded. Choose a new file to replace it.</p>
          )}
          {formErrors.video_file && <p className="text-red-500 text-xs mt-1">{formErrors.video_file}</p>}
        </div>
      )}
    </div>
  );

  const renderMeetingFields = () => (
    <>
      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-1">
          Meeting Link <span className="text-red-500">*</span>
        </label>
        <input
          type="url"
          value={formData.url_or_path}
          onChange={(e) => handleFieldChange('url_or_path', e.target.value)}
          className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${
            formErrors.url_or_path ? 'border-red-500' : 'border-purple-200 focus:ring-2 focus:ring-purple-500'
          }`}
          placeholder="https://teams.microsoft.com/... or Zoom / Google Meet link"
        />
        {formErrors.url_or_path && <p className="text-red-500 text-xs mt-1">{formErrors.url_or_path}</p>}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Meeting Date & Time <span className="text-red-500">*</span>
          </label>
          <input
            type="datetime-local"
            value={formData.meeting_datetime}
            onChange={(e) => handleFieldChange('meeting_datetime', e.target.value)}
            className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${
              formErrors.meeting_datetime ? 'border-red-500' : 'border-purple-200 focus:ring-2 focus:ring-purple-500'
            }`}
          />
          {formErrors.meeting_datetime && (
            <p className="text-red-500 text-xs mt-1">{formErrors.meeting_datetime}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Instructor <span className="text-red-500">*</span>
          </label>
          <select
            value={formData.instructor_id || ''}
            onChange={(e) => handleFieldChange('instructor_id', e.target.value)}
            className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${
              formErrors.instructor_id ? 'border-red-500' : 'border-purple-200 focus:ring-2 focus:ring-purple-500'
            }`}
          >
            <option value="">Select instructor</option>
            {instructors.map((instructor) => (
              <option key={instructor.id} value={instructor.id}>
                {`${instructor.first_name || ''} ${instructor.last_name || ''}`.trim() || instructor.email}
              </option>
            ))}
          </select>
          {instructors.length === 0 && (
            <p className="text-xs text-gray-500 mt-1">
              {hasOrganization
                ? 'No instructors found for the selected organization.'
                : 'No platform instructors available. Select an organization or add instructors.'}
            </p>
          )}
          {formErrors.instructor_id && (
            <p className="text-red-500 text-xs mt-1">{formErrors.instructor_id}</p>
          )}
        </div>
      </div>
    </>
  );

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Title <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => handleFieldChange('title', e.target.value)}
            className={`w-full px-3 py-2 rounded-lg border text-sm outline-none ${
              formErrors.title ? 'border-red-500' : 'border-purple-200 focus:ring-2 focus:ring-purple-500'
            }`}
            placeholder="Enter VLT title"
          />
          {formErrors.title && <p className="text-red-500 text-xs mt-1">{formErrors.title}</p>}
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Type</label>
          <select
            value={formData.type}
            onChange={(e) => onTypeChange(e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 text-sm outline-none"
          >
            <option value="video">Video</option>
            <option value="document">Document</option>
            <option value="link">Meeting Link</option>
          </select>
        </div>
      </div>

      <div className="mb-4">
        <label className="block text-sm font-semibold text-gray-700 mb-1">Description</label>
        <textarea
          rows={4}
          value={formData.description}
          onChange={(e) => handleFieldChange('description', e.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 text-sm outline-none resize-y min-h-[100px]"
          placeholder="Enter VLT description"
        />
      </div>

      {formData.type === 'document' && renderDocumentFields()}
      {formData.type === 'video' && renderVideoFields()}
      {formData.type === 'link' && renderMeetingFields()}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        {showOrganization && (
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">
              Organization <small className="text-muted">(optional)</small>
            </label>
            <select
              value={formData.organization_id || ''}
              onChange={(e) => handleOrganizationSelect(e.target.value)}
              disabled={orgFieldDisabled}
              className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 text-sm outline-none disabled:opacity-60"
            >
              <option value="">Global (No Organization)</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
            {hasGroups && (
              <p className="text-xs text-gray-500 mt-1">Clear selected groups to choose an organization.</p>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">
            Groups <small className="text-muted">(optional)</small>
          </label>
          <div className={`border border-purple-200 rounded-lg p-3 bg-white max-h-40 overflow-y-auto ${groupFieldDisabled ? 'opacity-60' : ''}`}>
            {groups.length === 0 ? (
              <p className="text-muted text-sm mb-0">No groups available.</p>
            ) : (
              groups.map((group) => {
                const groupId = String(group.id);
                return (
                  <Form.Check
                    key={group.id}
                    type="checkbox"
                    id={`vlt-group-${group.id}`}
                    label={group.name}
                    checked={(formData.group_ids || []).includes(groupId)}
                    onChange={() => handleGroupCheckbox(groupId)}
                    disabled={groupFieldDisabled}
                    className="mb-2"
                  />
                );
              })
            )}
          </div>
          <p className="text-xs text-gray-500 mt-1">
            {hasOrganization
              ? 'Clear organization to assign groups. Only learners in selected groups can view.'
              : 'Leave empty for global access. Selected groups restrict visibility to their learners.'}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Status</label>
          <select
            value={formData.status}
            onChange={(e) => handleFieldChange('status', e.target.value)}
            className="w-full px-3 py-2 rounded-lg border border-purple-200 focus:ring-2 focus:ring-purple-500 text-sm outline-none"
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-1">Thumbnail Image (Optional)</label>
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFieldChange('thumbnail_image', e.target.files?.[0] || null)}
            className="w-full px-3 py-2 rounded-lg border border-purple-200 text-sm"
          />
        </div>
      </div>
    </>
  );
};

export default VltFormFields;
